import React, { useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import UserSwitcher from './UserSwitcher';
import AppSidebar, { MobileNav, type NavItem } from './AppSidebar';
import {
    MagnifyingGlassIcon,
    DocumentTextIcon,
    CpuChipIcon,
    ChartBarIcon,
    ClockIcon,
    ListBulletIcon,
    WrenchScrewdriverIcon,
    LinkIcon,
    CalendarDaysIcon,
    QueueListIcon,
    TagIcon,
    UserGroupIcon,
    DocumentMagnifyingGlassIcon,
    PresentationChartBarIcon,
    GlobeAltIcon,
    Squares2X2Icon,
    ArrowTrendingUpIcon,
    ChatBubbleLeftRightIcon,
    DocumentDuplicateIcon,
    MagnifyingGlassCircleIcon,
    BeakerIcon,
    ChartBarSquareIcon,
    BugAntIcon,
    Bars3Icon,
} from '@heroicons/react/24/outline';
import { isGraderAllowedEmail } from './grader/graderEval';

interface AppLayoutProps {
    children: React.ReactNode;
}

const NAV_ITEMS: NavItem[] = [
    { path: '/explore', label: 'Explore', minRole: 'user', icon: MagnifyingGlassIcon },
    {
        path: '/clusters',
        label: 'Universe',
        minRole: 'eskimi_admin',
        icon: GlobeAltIcon,
        children: [
            { path: '/clusters/trending', label: 'Trending today', minRole: 'eskimi_admin', icon: ArrowTrendingUpIcon },
            { path: '/clusters/overview', label: 'Clusters', minRole: 'eskimi_admin', icon: Squares2X2Icon },
            { path: '/clusters/top-mentions', label: 'Top Mentions', minRole: 'eskimi_admin', icon: ChatBubbleLeftRightIcon },
            { path: '/clusters/briefs', label: 'My Briefs', minRole: 'eskimi_admin', icon: DocumentDuplicateIcon },
            { path: '/clusters/url-explorer', label: 'URL Explorer', minRole: 'eskimi_admin', icon: MagnifyingGlassCircleIcon },
        ],
    },
    { path: '/prompts', label: 'Brand Blueprints', minRole: 'user', icon: DocumentTextIcon },
    { path: '/scheduled-jobs', label: 'Scheduled Jobs', minRole: 'eskimi_admin', icon: CalendarDaysIcon },
    { path: '/media-planning', label: 'Media Planning', minRole: 'eskimi_admin', icon: PresentationChartBarIcon },
    { path: '/relevance-engine', label: 'Relevance Engine', minRole: 'user', icon: CpuChipIcon },
    { path: '/insights', label: 'Analysis Overview', minRole: 'eskimi_admin', icon: ChartBarIcon },
    { path: '/history', label: 'Analysis Audit', minRole: 'eskimi_admin', icon: ClockIcon },
    { path: '/site-lists', label: 'Site Lists', minRole: 'user', icon: ListBulletIcon },
    {
        label: 'Admin tools',
        minRole: 'admin',
        icon: WrenchScrewdriverIcon,
        children: [
            { path: '/admin/taxonomy', label: 'Taxonomy', minRole: 'admin', icon: TagIcon },
            { path: '/url-lookup', label: 'URL Lookup', minRole: 'admin', icon: LinkIcon },
            { path: '/admin/queue', label: 'Queue Admin', minRole: 'admin', icon: QueueListIcon },
            { path: '/admin/users', label: 'Manage Users', minRole: 'admin', icon: UserGroupIcon },
            { path: '/admin/audit', label: 'Audit', minRole: 'admin', icon: DocumentMagnifyingGlassIcon },
        ],
    },
    {
        label: 'Diagnostics',
        minRole: 'admin',
        icon: BugAntIcon,
        children: [
            { path: '/admin/job-performance', label: 'Job Performance', minRole: 'admin', icon: ChartBarSquareIcon },
        ],
    },
];

// The Prompt Grader nav entry is email-gated to a single allowlisted user (same
// gate as the page itself and the backend /api/grader surface). It is injected
// into the Diagnostics group only for that email; everyone else never sees it.
function buildNavItems(email: string | undefined | null): NavItem[] {
    if (!isGraderAllowedEmail(email)) {
        return NAV_ITEMS;
    }
    return NAV_ITEMS.map((item) => {
        if ('children' in item && item.label === 'Diagnostics') {
            return {
                ...item,
                children: [
                    ...item.children,
                    { path: '/admin/grader', label: 'Prompt Grader', minRole: 'admin', icon: BeakerIcon },
                ],
            };
        }
        return item;
    });
}

const SIDEBAR_STORAGE_KEY = 'eskimi-sidebar-open';

function getInitialSidebarOpen(): boolean {
    try {
        const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
        if (stored === null) return true;
        return stored === 'true';
    } catch {
        return true;
    }
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout, impersonation } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const [sidebarOpen, setSidebarOpen] = useState(getInitialSidebarOpen);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [showUserSwitcher, setShowUserSwitcher] = useState(false);
    const mobileTriggerRef = useRef<HTMLDivElement>(null);

    const handleSidebarToggle = () => {
        setSidebarOpen((prev) => {
            const next = !prev;
            try {
                localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next));
            } catch {
                // ignore
            }
            return next;
        });
    };

    const userRole = user?.role || 'user';
    const navItems = buildNavItems(user?.email);
    // Impersonating an admin hides admin-only chrome for the duration, same
    // as the old Header's isAdmin/canImpersonate gates.
    const isAdminUser = user?.role === 'admin' && !impersonation?.active;

    return (
        <div className="relative flex h-screen bg-canvas font-jakarta overflow-hidden">
            {/* Midnight & Lavender chrome reference: a single subtle purple
                corner glow, dark-mode only. -z-10 keeps it behind the
                (non-positioned) sidebar/content below, which paint on top of
                positioned siblings regardless of DOM order. */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-32 -right-32 -z-10 h-96 w-96 rounded-full bg-accent opacity-0 blur-[120px] dark:opacity-20"
            />
            <div
                className={`hidden lg:flex flex-shrink-0 overflow-hidden transition-[width] duration-200 ease-in-out flex-col min-h-0 ${sidebarOpen ? 'lg:w-[240px]' : 'lg:w-[62px]'}`}
            >
                <AppSidebar
                    isOpen={sidebarOpen}
                    onToggle={handleSidebarToggle}
                    items={navItems}
                    userRole={userRole}
                    currentPath={location.pathname}
                    onNavigate={navigate}
                    theme={theme}
                    onToggleTheme={toggleTheme}
                    user={user ? { name: user.name, email: user.email } : null}
                    onLogout={logout}
                    canImpersonate={isAdminUser}
                    onSwitchUser={() => setShowUserSwitcher(true)}
                />
            </div>
            {/* min-w-0 is load-bearing: without it this flex item defaults
                to min-width:auto and sizes itself to the page's min-content
                width, so one unbreakable string (a long URL) pushes the
                whole column past the viewport and the row's overflow-hidden
                clips the right-hand controls. */}
            <div className="flex-1 min-w-0 min-h-0 flex flex-col">
                {/* Mobile-only trigger for the nav drawer the sidebar replaces
                    on small screens — collapses to zero height on lg+, so the
                    desktop chrome stays sidebar-only. */}
                <div ref={mobileTriggerRef} className="flex-shrink-0 lg:hidden">
                    <button
                        type="button"
                        onClick={() => setMobileMenuOpen((prev) => !prev)}
                        className="m-2 p-2 rounded-lg text-ink-muted hover:bg-canvas hover:text-ink focus:outline-none focus:ring-2 focus:ring-accent"
                        aria-label="Open menu"
                    >
                        <Bars3Icon className="h-6 w-6" />
                    </button>
                </div>
                <MobileNav
                    isOpen={mobileMenuOpen}
                    onClose={() => setMobileMenuOpen(false)}
                    items={navItems}
                    userRole={userRole}
                    currentPath={location.pathname}
                    onNavigate={(path) => { navigate(path); setMobileMenuOpen(false); }}
                    anchorRef={mobileTriggerRef}
                    theme={theme}
                    onToggleTheme={toggleTheme}
                    user={user ? { name: user.name, email: user.email } : null}
                    onLogout={logout}
                    canImpersonate={isAdminUser}
                    onSwitchUser={() => setShowUserSwitcher(true)}
                />
                <main className="flex-1 min-h-0 overflow-auto w-full">
                    <div className="py-8 px-4 sm:px-6 lg:px-8 w-full h-full flex flex-col min-h-0">
                        <div className="flex items-end gap-3 mb-6 flex-shrink-0">
                            {/* Per-page context portal target (deepcontext-tpe1) — pages
                                mount their scoping control (e.g. the captioned Brand
                                Blueprint field) here via usePortalSlot('app-page-context').
                                Bottom-aligned (items-end on the row) so a captioned field
                                and a bare pill share one baseline. */}
                            <div id="app-page-context" className="min-w-0 flex-1 flex items-end gap-3" />
                            <div className="flex items-center gap-3 flex-shrink-0" data-testid="page-header-actions">
                                {/* Per-page actions portal target — pages mount
                                    content here via usePortalSlot('app-page-actions'). */}
                                <div id="app-page-actions" className="flex items-center gap-2" />
                            </div>
                        </div>
                        {children}
                    </div>
                </main>
            </div>
            {isAdminUser && (
                <UserSwitcher isOpen={showUserSwitcher} onClose={() => setShowUserSwitcher(false)} />
            )}
        </div>
    );
};

export default AppLayout;
